/**
 * BeezFy Premium Mobile Menu System
 * Ultra-premium mobile navigation for fashion e-commerce
 * Optimized for conversion and mobile-first experience
 */

class BeezfyMobileMenuPremium {
  constructor() {
    this.state = {
      isOpen: false,
      activeSubmenu: null,
      activeNestedSubmenu: null,
      searchQuery: '',
      currentLevel: 0,
      history: [],
      touchStartX: 0,
      touchStartY: 0,
      isAnimating: false
    };

    this.config = {
      animationDuration: 300,
      swipeThreshold: 50,
      searchDebounce: 300,
      megaMenuThreshold: 5,
      lazyLoadDelay: 100
    };

    this.elements = {};
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.cacheElements();
    this.enhanceMenuStructure();
    this.setupEventListeners();
    this.setupTouchGestures();
    this.setupSearch();
    this.setupAccountFeatures();
    this.setupLocalization();
    this.preloadImages();
    this.initializeAccessibility();
  }

  cacheElements() {
    this.elements = {
      drawer: document.querySelector('#beezfy-menu-drawer'),
      overlay: document.querySelector('.beezfy-drawer-overlay'),
      header: document.querySelector('.beezfy-drawer__header'),
      content: document.querySelector('.beezfy-drawer__content'),
      nav: document.querySelector('.menu-drawer__nav'),
      search: document.querySelector('.menu-drawer__search'),
      searchInput: document.querySelector('.search-form__input'),
      searchResults: null,
      account: document.querySelector('.menu-drawer__account'),
      localization: document.querySelector('.menu-drawer__localization'),
      social: document.querySelector('.menu-drawer__social'),
      triggers: document.querySelectorAll('[data-drawer-trigger="menu"]'),
      closeBtn: document.querySelector('[data-drawer-close]'),
      backButton: null
    };
  }

  enhanceMenuStructure() {
    if (!this.elements.drawer) return;

    // Add premium classes
    this.elements.drawer.classList.add('beezfy-menu-premium');

    // Enhance menu items with premium features
    this.enhanceMenuItems();

    // Add search results container
    this.createSearchResults();

    // Add promotional banner if configured
    this.addPromotionalBanner();

    // Add recently viewed products
    this.addRecentlyViewed();
  }

  enhanceMenuItems() {
    const menuItems = this.elements.nav?.querySelectorAll('.menu-drawer__item');

    menuItems?.forEach((item, index) => {
      const hasSubmenu = item.querySelector('.menu-drawer__submenu');

      if (hasSubmenu) {
        // Add slide animation attributes
        item.setAttribute('data-menu-index', index);
        hasSubmenu.setAttribute('data-submenu-level', '1');

        // Add image thumbnails for categories if available
        this.addCategoryImages(item);

        // Enhance nested submenus
        const nestedSubmenus = hasSubmenu.querySelectorAll('.menu-drawer__submenu--nested');
        nestedSubmenus.forEach((nested, nestedIndex) => {
          nested.setAttribute('data-submenu-level', '2');
          nested.setAttribute('data-nested-index', nestedIndex);
        });
      }

      // Add touch feedback
      item.addEventListener('touchstart', () => {
        item.classList.add('touch-active');
      });

      item.addEventListener('touchend', () => {
        setTimeout(() => item.classList.remove('touch-active'), 100);
      });
    });
  }

  addCategoryImages(menuItem) {
    const link = menuItem.querySelector('.menu-drawer__link');
    if (!link) return;

    // Category images disabled to avoid 404 errors
    // const categoryName = link.textContent.trim().toLowerCase();
    // const imageMap = {
    //   'women': '/assets/category-women.jpg',
    //   'men': '/assets/category-men.jpg',
    //   'accessories': '/assets/category-accessories.jpg',
    //   'sale': '/assets/category-sale.jpg'
    // };

    // if (imageMap[categoryName]) {
    //   const imgContainer = document.createElement('div');
    //   imgContainer.className = 'menu-drawer__item-image';
    //   imgContainer.innerHTML = `<img src="${imageMap[categoryName]}" alt="${link.textContent}" loading="lazy">`;
    //   menuItem.insertBefore(imgContainer, menuItem.firstChild);
    // }
  }

  createSearchResults() {
    const searchResults = document.createElement('div');
    searchResults.className = 'menu-drawer__search-results';
    searchResults.style.display = 'none';
    searchResults.innerHTML = `
      <div class="search-results__header">
        <span class="search-results__title">${window.theme?.strings?.searchResults || 'Search Results'}</span>
        <button class="search-results__close" aria-label="Close search">×</button>
      </div>
      <div class="search-results__content"></div>
    `;

    this.elements.search?.appendChild(searchResults);
    this.elements.searchResults = searchResults;
  }

  addPromotionalBanner() {
    if (!window.beezfySettings?.menuPromo) return;

    const banner = document.createElement('div');
    banner.className = 'menu-drawer__promo-banner';
    banner.innerHTML = `
      <div class="promo-banner__content">
        <span class="promo-banner__text">${window.beezfySettings.menuPromo.text}</span>
        ${window.beezfySettings.menuPromo.link ?
          `<a href="${window.beezfySettings.menuPromo.link}" class="promo-banner__link">
            ${window.beezfySettings.menuPromo.linkText || 'Shop Now'}
          </a>` : ''}
      </div>
    `;

    this.elements.content?.insertBefore(banner, this.elements.nav);
  }

  addRecentlyViewed() {
    const recentProducts = this.getRecentlyViewedProducts();
    if (!recentProducts || recentProducts.length === 0) return;

    const container = document.createElement('div');
    container.className = 'menu-drawer__recently-viewed';
    container.innerHTML = `
      <h3 class="recently-viewed__title">${window.theme?.strings?.recentlyViewed || 'Recently Viewed'}</h3>
      <div class="recently-viewed__products">
        ${recentProducts.map(product => `
          <a href="${product.url}" class="recently-viewed__product">
            <img src="${product.image}" alt="${product.title}" loading="lazy">
            <span class="recently-viewed__product-title">${product.title}</span>
            <span class="recently-viewed__product-price">${product.price}</span>
          </a>
        `).join('')}
      </div>
    `;

    this.elements.content?.appendChild(container);
  }

  getRecentlyViewedProducts() {
    try {
      const stored = localStorage.getItem('beezfy_recently_viewed');
      return stored ? JSON.parse(stored).slice(0, 4) : [];
    } catch {
      return [];
    }
  }

  setupEventListeners() {
    // Menu triggers
    this.elements.triggers?.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Close button
    this.elements.closeBtn?.addEventListener('click', () => this.close());

    // Overlay click
    this.elements.overlay?.addEventListener('click', () => this.close());


    // Submenu navigation
    this.setupSubmenuNavigation();

    // ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.state.isOpen) {
        this.close();
      }
    });

    // Prevent body scroll when menu is open
    this.elements.drawer?.addEventListener('touchmove', (e) => {
      if (this.state.isOpen && !this.isScrollable(e.target)) {
        e.preventDefault();
      }
    }, { passive: false });
  }

  setupSubmenuNavigation() {
    const menuLinks = this.elements.nav?.querySelectorAll('.menu-drawer__link--has-submenu');

    console.log('🔍 BeezFy Menu: Found', menuLinks?.length || 0, 'menu items with submenu');

    menuLinks?.forEach((link, index) => {
      console.log(`   📌 Menu ${index}:`, link.textContent.trim());

      link.addEventListener('click', (e) => {
        e.preventDefault();
        console.log('🔔 CLICK on:', link.textContent.trim());

        const item = link.closest('.menu-drawer__item');
        const submenu = item?.querySelector('.menu-drawer__submenu');

        console.log('   - Submenu found:', !!submenu);

        if (submenu) {
          this.toggleSubmenu(submenu, link);
        } else {
          console.error('   ❌ Submenu not found for:', link.textContent.trim());
        }
      });
    });

    // Nested submenus
    const nestedLinks = this.elements.nav?.querySelectorAll('.menu-drawer__submenu-link--has-submenu');

    nestedLinks?.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();

        const item = link.closest('.menu-drawer__item--nested');
        const submenu = item?.querySelector('.menu-drawer__submenu--nested');

        if (submenu) {
          this.toggleSubmenu(submenu, link);
        }
      });
    });

    // Handle regular links - close menu on click if enabled
    const regularLinks = this.elements.drawer?.querySelectorAll('.menu-drawer__link:not(.menu-drawer__link--has-submenu), .menu-drawer__submenu-link:not(.menu-drawer__submenu-link--has-submenu)');

    regularLinks?.forEach(link => {
      link.addEventListener('click', () => {
        if (window.beezfySettings?.menuCloseOnClick !== false) {
          setTimeout(() => this.close(), 150);
        }
      });
    });
  }

  toggleSubmenu(submenu, trigger) {
    console.log('🔄 toggleSubmenu called');
    console.log('   - Submenu:', submenu);
    console.log('   - Trigger:', trigger.textContent.trim());

    const isOpen = submenu.classList.contains('is-open');
    const icon = trigger.querySelector('.menu-drawer__icon');
    const isNested = submenu.classList.contains('menu-drawer__submenu--nested');

    console.log('   - Was open:', isOpen);
    console.log('   - Is nested:', isNested);

    // Auto-collapse other submenus at the same level
    if (!isOpen && window.beezfySettings?.menuAutoCollapse !== false) {
      const parent = isNested ?
        submenu.closest('.menu-drawer__submenu') :
        this.elements.nav;

      const siblingSubmenus = parent?.querySelectorAll(
        isNested ? '.menu-drawer__submenu--nested.is-open' : '.menu-drawer__submenu.is-open:not(.menu-drawer__submenu--nested)'
      );

      siblingSubmenus?.forEach(sibling => {
        if (sibling !== submenu) {
          sibling.classList.remove('is-open');
          const siblingTrigger = sibling.previousElementSibling;
          if (siblingTrigger) {
            siblingTrigger.setAttribute('aria-expanded', 'false');
            const siblingIcon = siblingTrigger.querySelector('.menu-drawer__icon');
            if (siblingIcon) {
              siblingIcon.style.transform = '';
            }
          }
        }
      });
    }

    if (isOpen) {
      submenu.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      if (icon) {
        icon.style.transform = '';
      }
    } else {
      submenu.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      if (icon) {
        icon.style.transform = 'rotate(90deg)';
      }
    }
  }


  setupTouchGestures() {
    let touchStartX = 0;
    let touchStartY = 0;
    let touchEndX = 0;
    let touchEndY = 0;

    this.elements.drawer?.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }, { passive: true });

    this.elements.drawer?.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].clientX;
      touchEndY = e.changedTouches[0].clientY;

      this.handleSwipe(touchStartX, touchEndX, touchStartY, touchEndY);
    }, { passive: true });
  }

  handleSwipe(startX, endX, startY, endY) {
    const diffX = startX - endX;
    const diffY = startY - endY;

    // Check if horizontal swipe
    if (Math.abs(diffX) > Math.abs(diffY)) {
      if (Math.abs(diffX) > this.config.swipeThreshold) {
        if (diffX > 0) {
          // Swipe left - close menu
          if (this.state.currentLevel === 0) {
            this.close();
          }
        } else {
          // Swipe right - go back
          if (this.state.currentLevel > 0) {
            this.navigateBack();
          }
        }
      }
    }
  }

  setupSearch() {
    if (!this.elements.searchInput) return;

    let searchTimeout;

    this.elements.searchInput.addEventListener('input', (e) => {
      clearTimeout(searchTimeout);
      this.state.searchQuery = e.target.value;

      if (this.state.searchQuery.length > 2) {
        searchTimeout = setTimeout(() => {
          this.performSearch(this.state.searchQuery);
        }, this.config.searchDebounce);
      } else {
        this.hideSearchResults();
      }
    });

    // Close search results
    this.elements.searchResults?.querySelector('.search-results__close')
      ?.addEventListener('click', () => this.hideSearchResults());
  }

  async performSearch(query) {
    try {
      const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5`);
      const data = await response.json();

      if (data.resources?.results?.products) {
        this.showSearchResults(data.resources.results.products);
      }
    } catch (error) {
      console.error('Search error:', error);
    }
  }

  showSearchResults(products) {
    if (!this.elements.searchResults) return;

    const content = this.elements.searchResults.querySelector('.search-results__content');

    if (products.length > 0) {
      content.innerHTML = products.map(product => `
        <a href="${product.url}" class="search-result__item">
          <img src="${product.featured_image}" alt="${product.title}" loading="lazy">
          <div class="search-result__details">
            <span class="search-result__title">${product.title}</span>
            <span class="search-result__price">${this.formatPrice(product.price)}</span>
          </div>
        </a>
      `).join('');
    } else {
      content.innerHTML = `<p class="search-results__empty">${window.theme?.strings?.noResults || 'No results found'}</p>`;
    }

    this.elements.searchResults.style.display = 'block';
  }

  hideSearchResults() {
    if (this.elements.searchResults) {
      this.elements.searchResults.style.display = 'none';
    }
  }

  formatPrice(cents) {
    const price = (cents / 100).toFixed(2);
    return window.theme?.moneyFormat ?
      window.theme.moneyFormat.replace('{{amount}}', price) :
      `$${price}`;
  }

  setupAccountFeatures() {
    // Add wishlist count if available
    const wishlistCount = window.BeezfyWishlist?.getCount?.() || 0;

    if (wishlistCount > 0) {
      const wishlistLink = document.createElement('a');
      wishlistLink.href = '/pages/wishlist';
      wishlistLink.className = 'menu-drawer__account-link menu-drawer__wishlist';
      wishlistLink.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
        </svg>
        <span>${window.theme?.strings?.wishlist || 'Wishlist'} (${wishlistCount})</span>
      `;

      this.elements.account?.insertBefore(wishlistLink, this.elements.account.firstChild);
    }
  }

  setupLocalization() {
    // Language selector
    const langSelector = this.elements.localization?.querySelector('[data-language-selector]');

    langSelector?.addEventListener('change', (e) => {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/localization';

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'locale_code';
      input.value = e.target.value;

      form.appendChild(input);
      document.body.appendChild(form);
      form.submit();
    });

    // Currency selector
    const currencySelector = this.elements.localization?.querySelector('[data-currency-selector]');

    currencySelector?.addEventListener('change', (e) => {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/localization';

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'country_code';
      input.value = e.target.value;

      form.appendChild(input);
      document.body.appendChild(form);
      form.submit();
    });
  }

  preloadImages() {
    // Preload category images for smooth transitions - DISABLED
    // const images = [
    //   '/assets/category-women.jpg',
    //   '/assets/category-men.jpg',
    //   '/assets/category-accessories.jpg',
    //   '/assets/category-sale.jpg'
    // ];

    const images = []; // Empty array - no images to preload
    images.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }

  initializeAccessibility() {
    // Set ARIA attributes
    this.elements.drawer?.setAttribute('role', 'navigation');
    this.elements.drawer?.setAttribute('aria-label', 'Mobile menu');

    // Trap focus when menu is open
    this.elements.drawer?.addEventListener('keydown', (e) => {
      if (e.key === 'Tab' && this.state.isOpen) {
        const focusableElements = this.elements.drawer.querySelectorAll(
          'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    });
  }

  isScrollable(element) {
    let el = element;
    while (el && el !== this.elements.drawer) {
      if (el.scrollHeight > el.clientHeight) {
        return true;
      }
      el = el.parentElement;
    }
    return false;
  }

  open() {
    if (this.state.isOpen || this.state.isAnimating) return;

    console.log('🔓 Opening menu...');

    this.state.isAnimating = true;
    this.state.isOpen = true;

    // Open drawer - standalone implementation
    if (this.elements.drawer) {
      this.elements.drawer.style.display = 'block';
      this.elements.drawer.setAttribute('aria-hidden', 'false');

      // Force reflow
      this.elements.drawer.offsetHeight;

      this.elements.drawer.classList.add('is-open');
      console.log('   ✅ Drawer opened');
    }

    // Show overlay
    if (this.elements.overlay) {
      this.elements.overlay.classList.add('is-visible');
      this.elements.overlay.style.display = 'block';
      console.log('   ✅ Overlay shown');
    }

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Emit event
    window.dispatchEvent(new CustomEvent('beezfy:menu:open'));

    // Focus on first focusable element
    setTimeout(() => {
      const firstFocusable = this.elements.drawer?.querySelector('button, a, input');
      firstFocusable?.focus();
      this.state.isAnimating = false;
      console.log('   ✅ Menu fully open');
    }, this.config.animationDuration);
  }

  close() {
    if (!this.state.isOpen || this.state.isAnimating) return;

    console.log('🔒 Closing menu...');

    this.state.isAnimating = true;
    this.state.isOpen = false;

    // Reset navigation
    this.resetNavigation();

    // Close drawer - standalone implementation
    if (this.elements.drawer) {
      this.elements.drawer.classList.remove('is-open');
      console.log('   ✅ Drawer closed');

      setTimeout(() => {
        this.elements.drawer.style.display = 'none';
        this.elements.drawer.setAttribute('aria-hidden', 'true');
      }, this.config.animationDuration);
    }

    // Hide overlay
    if (this.elements.overlay) {
      this.elements.overlay.classList.remove('is-visible');
      console.log('   ✅ Overlay hidden');

      setTimeout(() => {
        this.elements.overlay.style.display = 'none';
      }, this.config.animationDuration);
    }

    // Restore body scroll
    document.body.style.overflow = '';

    // Emit event
    window.dispatchEvent(new CustomEvent('beezfy:menu:close'));

    setTimeout(() => {
      this.state.isAnimating = false;
      console.log('   ✅ Menu fully closed');
    }, this.config.animationDuration);
  }

  resetNavigation() {
    // Close all submenus
    const allSubmenus = this.elements.drawer?.querySelectorAll('.menu-drawer__submenu');
    allSubmenus?.forEach(submenu => {
      submenu.classList.remove('is-open');
    });

    // Reset all aria-expanded attributes
    const allTriggers = this.elements.drawer?.querySelectorAll('[aria-expanded]');
    allTriggers?.forEach(trigger => {
      trigger.setAttribute('aria-expanded', 'false');
      const icon = trigger.querySelector('.menu-drawer__icon');
      if (icon) {
        icon.style.transform = '';
      }
    });

    // Hide search results
    this.hideSearchResults();

    // Clear search input
    if (this.elements.searchInput) {
      this.elements.searchInput.value = '';
    }
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.BeezfyMobileMenu = new BeezfyMobileMenuPremium();
  });
} else {
  window.BeezfyMobileMenu = new BeezfyMobileMenuPremium();
}